import React, { useState } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as inventoryService from "../../services/inventoryService";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

const TXN_TYPES = [
  { value: "PURCHASE_IN", label: "Stock In (Purchase)" },
  { value: "ADJUSTMENT_IN", label: "Adjustment (Increase)" },
  { value: "ADJUSTMENT_OUT", label: "Adjustment (Decrease)" },
  { value: "RETURN", label: "Return to stock" },
  { value: "WASTAGE", label: "Wastage / Damage" },
];

// Role=3 only, enforced by the caller and by the backend's requireRole(3).
function StockAdjustModal({ show, onHide, item, onPosted }) {
  const [txnType, setTxnType] = useState("PURCHASE_IN");
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState(null);
  const currency = getVendorCurrency();

  const isDecrease = txnType === "ADJUSTMENT_OUT" || txnType === "WASTAGE";
  const requiresCost = txnType === "PURCHASE_IN";

  const reset = () => {
    setTxnType("PURCHASE_IN");
    setQuantity("");
    setUnitCost("");
    setReason("");
    setNotes("");
    setWarning(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!item) return;
    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      toast.error("Enter a quantity greater than 0");
      return;
    }
    setSubmitting(true);
    try {
      const res = await inventoryService.postTransaction({
        item_id: item.id,
        txn_type: txnType,
        qty_change: isDecrease ? -qty : qty,
        unit_cost: requiresCost ? Number(unitCost) : undefined,
        reason,
        notes,
      });
      if (res?.success === false) {
        toast.error(res.message || "Could not post transaction");
      } else {
        if (res?.data?.warning) setWarning(res.data.warning);
        toast.success("Stock transaction posted");
        onPosted?.();
        reset();
        onHide();
      }
    } catch (err) {
      toast.error(err.message || "Could not post transaction");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => {
        reset();
        onHide();
      }}
      title={`Stock-In / Adjust — ${item?.name || ""}`}
      width={440}
    >
      <Form onSubmit={handleSubmit}>
        <div className="mb-3 text-muted" style={{ fontSize: 13 }}>
          Current quantity: <strong>{formatDecimal(item?.current_qty)}</strong> {item?.unit_of_measure}
        </div>

        {warning && <Alert variant="warning">{warning}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Transaction type</Form.Label>
          <Form.Select className="inventory-select" value={txnType} onChange={(e) => setTxnType(e.target.value)}>
            {TXN_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            type="number"
            step="0.001"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
          />
        </Form.Group>

        {requiresCost && (
          <Form.Group className="mb-3">
            <Form.Label>Unit cost ({currency})</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              required
            />
            <Form.Text className="text-muted">
              Recalculates the item's cached cost per the vendor's costing method (WAC/LATEST).
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Reason</Form.Label>
          <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Notes</Form.Label>
          <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Form.Group>

        {isDecrease && Number(quantity) > Number(item?.current_qty || 0) && (
          <Alert variant="warning">
            This will take stock below zero. You may proceed — negative stock is allowed and will be flagged.
          </Alert>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} style={{ background: "var(--button-color)", border: "none", color: "#fff" }}>
            {submitting ? "Posting…" : "Post Transaction"}
          </Button>
        </div>
      </Form>
    </SlidePanel>
  );
}

export default StockAdjustModal;
